import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface Command {
  id: string;
  label: string;
  description: string;
  icon?: string;
}

interface NavCommand extends Command {
  kind: 'nav';
  path: string;
}

interface ActionCommand extends Command {
  kind: 'action';
  action: () => void;
}

type Cmd = NavCommand | ActionCommand;

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = COMMANDS.filter(
    (c) =>
      c.label.toLowerCase().includes(query.toLowerCase()) ||
      c.description.toLowerCase().includes(query.toLowerCase())
  );

  const execute = useCallback(
    (cmd: Cmd) => {
      if (cmd.kind === 'nav') {
        navigate(cmd.path);
      } else {
        cmd.action();
      }
      onClose();
    },
    [navigate, onClose]
  );

  // Reset selectedIdx when query changes
  useEffect(() => {
    setSelectedIdx(0);
  }, [query]);

  // Reset state on close
  useEffect(() => {
    if (!open) {
      setQuery('');
      setSelectedIdx(0);
    }
  }, [open]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIdx((idx) => (filtered.length === 0 ? 0 : (idx + 1) % filtered.length));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIdx((idx) =>
          filtered.length === 0 ? 0 : (idx - 1 + filtered.length) % filtered.length
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const cmd = filtered[selectedIdx];
        if (cmd) {
          execute(cmd);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, filtered, selectedIdx, execute, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const item = listRef.current.children[selectedIdx] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [selectedIdx]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '15vh',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 520,
          background: 'var(--ix-bg-elevated)',
          borderRadius: 12,
          border: '1px solid var(--ix-border-subtle)',
          overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0,0,0,0.4)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          id="command-palette-query"
          name="commandQuery"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher une commande..."
          style={{
            width: '100%',
            padding: '14px 16px',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            fontSize: 15,
            color: 'var(--ix-text-primary)',
            borderBottom: '1px solid var(--ix-border-subtle)',
            boxSizing: 'border-box',
          }}
        />

        <div
          ref={listRef}
          style={{ maxHeight: 320, overflowY: 'auto' }}
        >
          {filtered.length === 0 ? (
            <div
              style={{
                padding: '20px 16px',
                textAlign: 'center',
                fontSize: 13,
                color: 'var(--ix-text-tertiary)',
              }}
            >
              Aucun résultat pour &ldquo;{query}&rdquo;
            </div>
          ) : (
            filtered.map((cmd, idx) => {
              const isSelected = idx === selectedIdx;
              return (
                <div
                  key={cmd.id}
                  onClick={() => execute(cmd)}
                  onMouseEnter={() => setSelectedIdx(idx)}
                  style={{
                    padding: '10px 16px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: isSelected
                      ? 'color-mix(in srgb, var(--ix-accent) 12%, transparent)'
                      : 'transparent',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: 'var(--ix-text-primary)',
                      }}
                    >
                      {cmd.label}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--ix-text-tertiary)',
                      }}
                    >
                      {cmd.description}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 10,
                      color: 'var(--ix-text-tertiary)',
                      opacity: 0.6,
                    }}
                  >
                    ↵
                  </span>
                </div>
              );
            })
          )}
        </div>

        <div
          style={{
            padding: '8px 16px',
            borderTop: '1px solid var(--ix-border-subtle)',
            fontSize: 10,
            color: 'var(--ix-text-tertiary)',
            display: 'flex',
            gap: 16,
          }}
        >
          <span>↑↓ naviguer</span>
          <span>↵ ouvrir</span>
          <span>esc fermer</span>
        </div>
      </div>
    </div>
  );
}

const COMMANDS: Cmd[] = [
  // Navigation – pages principales
  { kind: 'nav', id: 'accueil', label: 'Accueil', description: 'Dashboard principal', path: '/' },
  { kind: 'nav', id: 'missions', label: 'Missions', description: 'Toutes vos missions', path: '/missions' },
  { kind: 'nav', id: 'finance', label: 'Finance', description: 'Trésorerie, simulations', path: '/finance' },
  { kind: 'nav', id: 'moi', label: 'Moi', description: 'Profil, réglages', path: '/moi' },

  // Navigation – outils
  { kind: 'nav', id: 'missio', label: 'Cockpit missions', description: 'Pipeline et pilotage', path: '/tools/missio' },
  { kind: 'nav', id: 'mission-memory', label: 'Retours missions', description: 'Carnet des missions passées', path: '/tools/mission-memory' },
  { kind: 'nav', id: 'calc', label: 'Chiffrer une mission', description: 'TJM et net mensuel', path: '/tools/calc-mission' },
  { kind: 'nav', id: 'goal', label: 'Mon TJM', description: 'TJM cible selon votre objectif', path: '/tools/calc-mission/goal' },
  { kind: 'nav', id: 'project', label: 'Projet forfait', description: 'Budget fixe → TJM implicite', path: '/tools/calc-mission/project' },
  { kind: 'nav', id: 'scratchpad', label: 'Éditeur de formules', description: 'Calculs libres', path: '/tools/scratchpad' },

  { kind: 'nav', id: 'fiscal', label: 'Optimisation fiscale', description: 'Leviers selon votre statut', path: '/tools/fiscal' },
  { kind: 'nav', id: 'benchmark', label: 'Comparer au marché (détaillé)', description: 'Par secteur, version enrichie', path: '/tools/benchmark' },
  { kind: 'nav', id: 'tjm-radar', label: 'Comparer au marché', description: 'Estimation réaliste selon profil', path: '/tools/tjm-radar' },
  { kind: 'nav', id: 'tresorerie', label: 'Trésorerie', description: 'Entrées et sorties', path: '/tools/tresorerie' },
  { kind: 'nav', id: 'runway', label: 'Réserve', description: 'Runway et scénarios', path: '/tools/runway' },
  { kind: 'nav', id: 'reporting', label: 'Reporting', description: 'KPIs, CA mensuel, top clients', path: '/tools/reporting' },
  { kind: 'nav', id: 'projections', label: 'Projections', description: 'CA 12 mois, 3 scénarios', path: '/tools/projections' },
  { kind: 'nav', id: 'notes', label: 'Notes', description: 'Notes cross-missions', path: '/tools/notes' },
  { kind: 'nav', id: 'devis', label: 'Devis', description: 'Créer un devis', path: '/tools/devis' },
  { kind: 'nav', id: 'proposal', label: 'Proposition', description: 'Proposition de mission', path: '/tools/proposal' },
  { kind: 'nav', id: 'deal-room', label: 'Partage client', description: 'Présentation client', path: '/tools/deal-room' },
  { kind: 'nav', id: 'estimation', label: 'Chiffrage détaillé', description: 'Chiffrer un projet par fonctionnalité', path: '/tools/estimation' },
  { kind: 'nav', id: 'cra', label: 'Agenda', description: 'Jours travaillés', path: '/tools/cra' },
  { kind: 'nav', id: 'new-mission', label: 'Nouvelle mission', description: 'Création rapide', path: '/missions/new' },
  { kind: 'nav', id: 'eval', label: 'Évaluer une opportunité', description: 'GO / NO-GO avant de vous engager', path: '/tools/eval' },
  { kind: 'nav', id: 'sipoc', label: 'Périmètre', description: 'Ce qu\'on livre', path: '/tools/sipoc' },
  { kind: 'nav', id: 'moscow', label: 'Priorités', description: 'Choisir le plus important', path: '/tools/moscow' },
  { kind: 'nav', id: 'roles', label: 'Rôles', description: 'Qui fait quoi', path: '/tools/roles' },
  { kind: 'nav', id: 'raci', label: 'Matrice RACI', description: 'Responsabilités', path: '/tools/raci' },
  { kind: 'nav', id: 'profil', label: 'Profil', description: 'Informations professionnelles', path: '/tools/profile' },
];
